import { useCallback } from 'react';
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConstantActions } from '../../../../Services/_actions/constant.actions';
import useAppNavigation from '../../../../hooks/useAppNavigation';
import { navigateToRoute } from '../../../../utils/navigation/resolveActiveRoute';
import { SIDEBAR_WIDTH } from '../../constants/appShell.constants';
import { useSidebarLayout } from '../../context/SidebarProvider';
import useAppShellSx from '../../hooks/useAppShellSx';
import useSidebarTransition from '../../hooks/useSidebarTransition';
import SidebarBrand from './SidebarBrand';
import SidebarCollapseToggle from './SidebarCollapseToggle';
import SidebarSection from './SidebarSection';
import SidebarUserFooter from './SidebarUserFooter';

export default function AppSidebar({ mobileOpen, onMobileClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const actions = ConstantActions();
  const sx = useAppShellSx();
  const { widthTransition } = useSidebarTransition();

  const { collapsed, width, toggleCollapsed } = useSidebarLayout();
  const {
    sections,
    displayName,
    profileName,
    organisationName,
    onConceptBuilderExpand,
  } = useAppNavigation();

  const handleNavigate = useCallback(
    (route) => {
      navigateToRoute(route, navigate);
      if (isMobile) {
        onMobileClose?.();
      }
    },
    [isMobile, navigate, onMobileClose]
  );

  const handleNavigateHome = useCallback(() => {
    const home = actions.getDynamicHomePageLink?.();
    if (home) {
      handleNavigate(home);
    }
  }, [actions, handleNavigate]);

  const renderDrawerContent = (isCollapsedView) => (
    <Box sx={sx.sidebarRoot}>
      <SidebarBrand
        collapsed={isCollapsedView}
        organisationName={organisationName}
        onNavigateHome={handleNavigateHome}
      />

      <Box sx={sx.sidebarScroll}>
        {sections.map((section) => (
          <SidebarSection
            key={section.title || section.items?.[0]?.key || 'section'}
            section={section}
            collapsed={isCollapsedView}
            pathname={location.pathname}
            onNavigate={handleNavigate}
            onConceptBuilderExpand={onConceptBuilderExpand}
          />
        ))}
      </Box>

      <SidebarUserFooter
        collapsed={isCollapsedView}
        displayName={displayName}
        profileName={profileName}
        onNavigate={handleNavigate}
        onLogout={() => actions.logout?.()}
      />
    </Box>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': sx.drawerTemporaryPaper,
        }}
      >
        {renderDrawerContent(false)}
      </Drawer>

      <Box
        sx={{
          ...sx.sidebarWrapper,
          width,
          display: { xs: 'none', md: 'block' },
          transition: widthTransition,
          willChange: 'width',
        }}
      >
        <Drawer
          variant="permanent"
          open
          sx={{
            width,
            flexShrink: 0,
            transition: widthTransition,
            '& .MuiDrawer-paper': {
              ...sx.drawerPaper(width),
              width,
              transition: widthTransition,
              willChange: 'width',
            },
          }}
        >
          {renderDrawerContent(collapsed)}
        </Drawer>
        <SidebarCollapseToggle collapsed={collapsed} onToggle={toggleCollapsed} />
      </Box>
    </>
  );
}

AppSidebar.propTypes = {
  mobileOpen: PropTypes.bool,
  onMobileClose: PropTypes.func,
};

export { SIDEBAR_WIDTH };
